// src/components/notifications/AccessRequestNotification.jsx
// Notification component for access request alerts with minimal payload

import { useState } from 'react';
import { 
  Users, 
  Clock, 
  FileText, 
  Check, 
  X, 
  Eye,
  MessageCircle
} from 'lucide-react';

const AccessRequestNotification = ({ 
  notification, 
  onApprove, 
  onDeny, 
  onView, 
  onMarkRead,
  className = '' 
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const { payload } = notification;
  const isBatch = payload.count > 1;
  const isRead = !!notification.readAt;

  // Handle quick approve
  const handleQuickApprove = async (e) => {
    e.stopPropagation();
    setIsProcessing(true);
    
    try {
      if (isBatch) {
        await onApprove(payload.batchId, '');
      } else {
        await onApprove(payload.requestId, '');
      }
      
      if (!isRead) {
        await onMarkRead(notification.id);
      }
    } catch (error) {
      console.error('Error approving request:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle quick deny
  const handleQuickDeny = async (e) => {
    e.stopPropagation();
    setIsProcessing(true);
    
    try {
      if (isBatch) {
        await onDeny(payload.batchId, '');
      } else {
        await onDeny(payload.requestId, '');
      }
      
      if (!isRead) {
        await onMarkRead(notification.id);
      }
    } catch (error) {
      console.error('Error denying request:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle view details
  const handleViewDetails = async (e) => {
    e.stopPropagation();
    
    if (!isRead) {
      await onMarkRead(notification.id);
    }
    
    onView(payload);
  };

  // Format time ago
  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const time = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const diffMs = now - time;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return time.toLocaleDateString();
  };

  return (
    <div 
      className={`border border-border rounded-lg transition-all duration-200 hover:shadow-sm ${
        isRead ? 'bg-surface' : 'bg-primary-light border-primary'
      } ${className}`}
      onClick={() => setIsExpanded(!isExpanded)}
    >
      {/* Notification header */}
      <div className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-start space-x-3">
            <div className={`p-2 rounded-full ${isRead ? 'bg-surface-hover' : 'bg-primary'}`}>
              <Users className={`w-4 h-4 ${isRead ? 'text-text-secondary' : 'text-white'}`} />
            </div>
            
            <div className="flex-1">
              <div className="flex items-center space-x-2 mb-1">
                <h4 className={`font-medium ${isRead ? 'text-text' : 'text-primary'}`}>
                  Access Request {isBatch ? `(${payload.count} filings)` : ''}
                </h4>
                {!isRead && (
                  <div className="w-2 h-2 bg-primary rounded-full"></div>
                )}
              </div>
              
              <div className="space-y-1">
                <p className="text-sm text-text-secondary">
                  <strong className="text-text">{payload.counselName || 'Counsel'}</strong> requested access
                  {isBatch ? ` to ${payload.count} filings` : ` to "${payload.filingTitle}"`}
                </p>
                
                <div className="flex items-center space-x-4 text-xs text-text-secondary">
                  <span className="flex items-center space-x-1">
                    <Clock className="w-3 h-3" />
                    <span>{formatTimeAgo(notification.createdAt)}</span>
                  </span>
                  
                  {payload.caseNumber && (
                    <span>Case #{payload.caseNumber}</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Quick actions */}
          <div className="flex items-center space-x-2">
            <button
              onClick={handleQuickApprove}
              disabled={isProcessing}
              className="p-2 rounded-lg bg-success text-white hover:bg-success-hover disabled:opacity-50 transition-colors"
              title="Quick approve"
            >
              <Check className="w-4 h-4" />
            </button>
            
            <button
              onClick={handleQuickDeny}
              disabled={isProcessing}
              className="p-2 rounded-lg bg-error text-white hover:bg-error-hover disabled:opacity-50 transition-colors"
              title="Quick deny"
            >
              <X className="w-4 h-4" />
            </button>
            
            <button
              onClick={handleViewDetails}
              className="p-2 rounded-lg bg-surface-hover text-text hover:bg-surface-hover transition-colors"
              title="View details"
            >
              <Eye className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Expanded details */}
        {isExpanded && (
          <div className="mt-4 pt-4 border-t border-border space-y-3">
            {/* Request details */}
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-text-secondary">Requested by:</span>
                <p className="text-text font-medium">{payload.counselName || 'Unknown Counsel'}</p>
              </div>
              
              <div>
                <span className="text-text-secondary">Requested at:</span>
                <p className="text-text">{new Date(notification.createdAt.toDate()).toLocaleString()}</p>
              </div>
              
              {payload.permissions && (
                <div className="col-span-2">
                  <span className="text-text-secondary">Permissions requested:</span>
                  <div className="flex items-center space-x-3 mt-1">
                    {payload.permissions.view && (
                      <span className="flex items-center space-x-1 text-text">
                        <Eye className="w-3 h-3" />
                        <span>View</span>
                      </span>
                    )}
                    {payload.permissions.download && (
                      <span className="flex items-center space-x-1 text-text">
                        <span>Download</span>
                      </span>
                    )}
                    {payload.permissions.print && (
                      <span className="flex items-center space-x-1 text-text">
                        <span>Print</span>
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Request note */}
            {payload.note && (
              <div className="p-3 bg-surface-hover rounded border-l-4 border-primary">
                <div className="flex items-start space-x-2">
                  <MessageCircle className="w-4 h-4 text-primary mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-text">Note from Counsel:</p>
                    <p className="text-sm text-text-secondary mt-1">{payload.note}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Filing list (for batch requests) */}
            {isBatch && payload.filingTitles && (
              <div>
                <p className="text-sm font-medium text-text mb-2">Requested filings:</p>
                <div className="space-y-1 max-h-32 overflow-y-auto">
                  {payload.filingTitles.slice(0, 5).map((title, index) => (
                    <div key={index} className="flex items-center space-x-2 text-sm text-text-secondary">
                      <FileText className="w-3 h-3" />
                      <span>{title}</span>
                    </div>
                  ))}
                  {payload.filingTitles.length > 5 && (
                    <p className="text-xs text-text-secondary">
                      ... and {payload.filingTitles.length - 5} more filings
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Action buttons */}
            <div className="flex items-center justify-end space-x-3 pt-2">
              <button
                onClick={handleViewDetails}
                className="flex items-center space-x-2 px-3 py-2 text-text-secondary hover:text-text transition-colors"
              >
                <Eye className="w-4 h-4" />
                <span>View in Manager</span>
              </button>
              
              <button
                onClick={handleQuickDeny}
                disabled={isProcessing}
                className="flex items-center space-x-2 px-3 py-2 bg-error text-white rounded hover:bg-error-hover disabled:opacity-50 transition-colors"
              >
                <X className="w-4 h-4" />
                <span>Deny</span>
              </button>
              
              <button
                onClick={handleQuickApprove}
                disabled={isProcessing}
                className="flex items-center space-x-2 px-3 py-2 bg-success text-white rounded hover:bg-success-hover disabled:opacity-50 transition-colors"
              >
                <Check className="w-4 h-4" />
                <span>Approve</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AccessRequestNotification;